import React, { useContext } from 'react';
import { CheckIcon, DeleteIcon, EditIcon, XSmallIcon } from '@shopify/polaris-icons';
import { Button, Card, Text, InlineError, InlineStack, IndexTable, useBreakpoints, EmptySearchResult, TextField } from '@shopify/polaris';
import { useToast } from '#client/components/common/Toast/ToastContext';
import { useTransferStore, useTransferTable } from '#client/stores/transfer/useTransferStore';
import { SessionContext } from '#client/contexts/SessionContext.jsx';
import './TransferItemListing.css';
import TRANSFER_STRINGS from '#client/pages/Transfers/constants/transfers.en.json';

export default function TransferItemEntry() {
    const { editingIndex, editingValue, mode, lines, handleDeleteItem, lastAdded, locationFromId, type, handleEditItem, handleCancelEdit, handleManualUpdateOfTotal, handleQtyChange, isValidQty } =
        useTransferTable();

    const { handleDeleteLine } = useTransferStore();

    const hasPermissionForActionOnTransfer = useTransferStore.use.hasPermissionForActionOnTransfer();
    const { currentUser } = useContext(SessionContext);
    const { addToast } = useToast();

    const status = useTransferStore.use.status();
    const showReason = status === 'Exception';
    const totalUnits = lines.reduce((total, line) => total + Number(line.qtySent ?? 0), 0);

    let colHeadings = [
        { title: 'SKU', key: 'sku' },
        { title: 'Barcode', key: 'barcode' },
        { title: 'Item Name', key: 'title' },
        { title: 'QTY Sent', key: 'qtySent' },
        { title: 'QTY Received', key: 'qtyReceived' },
    ];

    if (showReason) {
        colHeadings.push({ title: 'Reason', key: 'reason' });
    }

    if (mode == 'create' || (mode == 'edit' && status != 'Initiated' && type != 'Automatic') || mode == 'receive') {
        colHeadings.push({ title: 'Actions', key: 'actions' });
    }
    const tableHeadings = colHeadings.map((column) => ({
        title: column.title,
    }));

    const getRowClass = (entry) => {
        let classes = [];

        if (isLastAddedItem(entry)) {
            classes.push('row-flash-green');
        }

        if (entry.status == 'ARCHIVED') {
            classes.push('deleted-item-row');
        }

        //leave early, other classes pertain to receiving only
        if (mode != 'receive') {
            return classes.join(' ');
        }

        if ((entry.qtyReceived ?? 0) < entry.qtySent) {
            classes.push('below-quantity-received-row');
        } else if (entry.qtyReceived > (entry.qtySent ?? 0)) {
            classes.push('overreceived-row');
        } else if (entry.qtyReceived == entry.qtySent) {
            classes.push('success-quantity-received-row');
        }

        return classes.join(' ');
    };

    const isInvalid = isValueInvalid();
    const errorMessage = isInvalid ? 'Quantity must be greater than 0' : '';

    const isLastAddedItem = (entry) => {
        return lastAdded && entry.sku === lastAdded.sku;
    };

    const emptyStateMarkup = (
        <EmptySearchResult title={TRANSFER_STRINGS.TRANSFERS_MANAGEMENT.EMPTY_STATE_MARKUP_TITLE} description={TRANSFER_STRINGS.TRANSFERS_MANAGEMENT.EMPTY_STATE_MARKUP_DESCRIPTION} withIllustration />
    );

    const resourceName = {
        singular: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.TRANSFER,
        plural: TRANSFER_STRINGS.TRANSFERS_DASHBOARD.TRANSFERS,
    };

    const rowMarkup = lines.map((line, index) => {
        const rowClass = getRowClass(line);

        return (
            <IndexTable.Row id={line.id} key={line.id} position={index}>
                <IndexTable.Cell className={rowClass}>
                    <Text as="span">{line.sku}</Text>
                </IndexTable.Cell>
                <IndexTable.Cell className={rowClass}>
                    <Text as="span">{line.barcode}</Text>
                </IndexTable.Cell>
                <IndexTable.Cell className={rowClass}>
                    <Text as="span">
                        {line.title} {line.status == 'ARCHIVED' ? '- Deleted' : null}
                    </Text>
                </IndexTable.Cell>
                <IndexTable.Cell className={rowClass}>
                    {editingIndex == index && (mode == 'edit' || mode == 'create') ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {/* Wrap the Textfield in a div with an onKeydown event since polaris does not natively support onKeydown for its textfield elements */}
                            <div
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        if (isValidQty(index, line)) {
                                            handleManualUpdateOfTotal(index, line, addToast);
                                        }
                                    }
                                }}
                            >
                                <TextField
                                    value={editingValue}
                                    autoFocus
                                    onChange={(val) => handleQtyChange(index, val)}
                                    error={isInvalid}
                                    type="text"
                                    min="0"
                                    labelHidden
                                    style={{ width: 60 }}
                                    selectTextOnFocus
                                />
                                <span style={{ marginTop: '4px' }}>
                                    <InlineError message={errorMessage} />
                                </span>
                            </div>
                            <Button
                                icon={CheckIcon}
                                variant="monochromePlain"
                                onClick={() => {
                                    handleManualUpdateOfTotal(index, line, addToast);
                                }}
                                accessibilityLabel="Save"
                                disabled={!isValidQty(index, line)}
                            />
                            <Button icon={XSmallIcon} variant="monochromePlain" onClick={handleCancelEdit} accessibilityLabel="Cancel" />
                        </div>
                    ) : (
                        <Text as="span">{line.qtySent}</Text>
                    )}
                </IndexTable.Cell>
                <IndexTable.Cell className={rowClass}>
                    {editingIndex == index && mode == 'receive' ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {/* Wrap the Textfield in a div with an onKeydown event since polaris does not natively support onKeydown for its textfield elements */}
                            <div
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        if (isValidQty(index, line)) {
                                            handleManualUpdateOfTotal(index, line, addToast);
                                        }
                                    }
                                }}
                            >
                                <TextField
                                    value={editingValue}
                                    autoFocus
                                    onChange={(val) => handleQtyChange(index, val)}
                                    error={isInvalid}
                                    type="text"
                                    min="0"
                                    labelHidden
                                    style={{ width: 60 }}
                                    selectTextOnFocus
                                />
                                <span style={{ marginTop: '4px' }}>
                                    <InlineError message={errorMessage} />
                                </span>
                            </div>
                            <Button
                                icon={CheckIcon}
                                variant="monochromePlain"
                                onClick={() => {
                                    handleManualUpdateOfTotal(index, line, addToast);
                                }}
                                accessibilityLabel="Save"
                                disabled={!isValidQty(index, line)}
                            />
                            <Button icon={XSmallIcon} variant="monochromePlain" onClick={handleCancelEdit} accessibilityLabel="Cancel" />
                        </div>
                    ) : (
                        <Text as="span">{line.qtyReceived}</Text>
                    )}
                </IndexTable.Cell>
                {showReason && (
                    <IndexTable.Cell className={rowClass}>
                        <Text as="span">{line.qtyReceived < line.qtySent ? `Short ${line.qtySent - line.qtyReceived} of ${line.qtySent}` : ''}</Text>
                    </IndexTable.Cell>
                )}
                {(mode == 'create' ||
                    (mode == 'edit' && status != 'Initiated' && type != 'Automatic' && hasPermissionForActionOnTransfer(locationFromId, currentUser.locationIds)) ||
                    mode == 'receive') && (
                    <IndexTable.Cell className={rowClass}>
                        {/* DELETE BUTTON — removes entire line, only in Draft or Initiated */}
                        {(status === 'Draft' || status === 'Initiated') && <Button icon={DeleteIcon} variant="plain" onClick={() => handleDeleteLine(index)} accessibilityLabel="Delete Item" />}

                        {/* DELETE QUANTITY BUTTON — only for receive mode and qtyReceived > 0 */}
                        {mode === 'receive' && line.qtyReceived > 0 && (
                            <Button icon={DeleteIcon} variant="plain" onClick={() => handleDeleteItem(index)} accessibilityLabel="Delete Received Quantity" />
                        )}

                        {/* EDIT BUTTON — always available when Actions column is visible */}
                        <Button icon={EditIcon} variant="plain" onClick={() => handleEditItem(index, line)} accessibilityLabel="Edit Item" />
                    </IndexTable.Cell>
                )}
            </IndexTable.Row>
        );
    });

    return (
        <>
            <div className="transfer-item-listing">
                <Card>
                    <InlineStack align="space-between" blockAlign="center">
                        {mode === 'receive' ? <Text variant="headingLg">Items to Receive</Text> : <Text variant="headingLg">Items to Transfer</Text>}
                        {mode !== 'receive' && (
                            <Text as="span" variant="bodyLg" fontWeight="semibold">
                                Total: {totalUnits}
                            </Text>
                        )}
                    </InlineStack>
                    <IndexTable condensed={useBreakpoints().smDown} emptyState={emptyStateMarkup} resourceName={resourceName} itemCount={lines.length} headings={tableHeadings} selectable={false}>
                        {rowMarkup}
                    </IndexTable>
                </Card>
            </div>
        </>
    );

    function isValueInvalid() {
        if (editingIndex == null) {
            return true;
        }

        return !isValidQty(editingIndex, lines[editingIndex]);
    }
}
